import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Truck, Factory, HardHat, Wheat, Ship, Pickaxe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { INDUSTRIES } from '../data/industries';
import { getProductBySlug } from '../data/products';
import Seo from '../components/common/Seo';
import { routeMeta } from '../routeMeta';

const iconMap = {
  Pickaxe: Pickaxe,
  Truck: Truck,
  Factory: Factory,
  HardHat: HardHat,
  Wheat: Wheat,
  Ship: Ship,
};

const IndustriesPage = () => {
  return (
    <div data-testid="industries-page">
      <Seo {...routeMeta['/industries']} path="/industries" />
      {/* Hero Section */}
      <section className="bg-[#0B1F3B] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="flex items-center justify-center gap-2 text-sm text-slate-400 mb-6">
              <Link to="/" className="hover:text-white">Home</Link>
              <span>/</span>
              <span className="text-white">Industries</span>
            </div>
            <h1 className="font-['Manrope'] text-4xl md:text-6xl font-extrabold text-white uppercase tracking-tight">
              Industries We Serve
            </h1>
            <p className="mt-6 text-xl text-slate-300 max-w-3xl mx-auto">
              Tailored weighing solutions for every sector. From mining to manufacturing, 
              we understand your industry's unique challenges.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {INDUSTRIES.map((industry, index) => {
              const IconComponent = iconMap[industry.icon] || Factory;
              return (
                <motion.div
                  key={industry.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
                  data-testid={`industry-section-${industry.id}`}
                >
                  {/* Content */}
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-14 h-14 bg-[#1E40AF] flex items-center justify-center">
                        <IconComponent className="h-7 w-7 text-white" />
                      </div>
                      <h2 className="font-['Manrope'] text-3xl md:text-4xl font-bold text-[#0B1F3B] uppercase tracking-tight">
                        {industry.name}
                      </h2>
                    </div>
                    <p className="text-lg text-slate-600 leading-relaxed">
                      {industry.description}
                    </p>

                    {/* Challenges & Solutions */}
                    <div className="mt-8 grid md:grid-cols-2 gap-8">
                      <div>
                        <h4 className="font-bold text-[#0B1F3B] uppercase tracking-wider text-sm mb-4">
                          Industry Challenges
                        </h4>
                        <ul className="space-y-3">
                          {industry.challenges.map((challenge, i) => (
                            <li key={i} className="flex items-start gap-3 text-slate-600">
                              <span className="text-red-500 mt-1">•</span>
                              {challenge}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-bold text-[#0B1F3B] uppercase tracking-wider text-sm mb-4">
                          Our Solutions
                        </h4>
                        <ul className="space-y-3">
                          {industry.solutions.map((solution, i) => (
                            <li key={i} className="flex items-start gap-3 text-slate-600">
                              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                              {solution}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="mt-8 flex flex-wrap gap-6">
                      {Object.entries(industry.stats).map(([key, value]) => (
                        <div key={key} className="bg-white px-6 py-4 border border-slate-200">
                          <div className="text-2xl font-bold text-[#1E40AF] font-['JetBrains_Mono']">{value}</div>
                          <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* CTA */}
                    <div className="mt-8">
                      <Link to="/contact">
                        <Button className="bg-[#0B1F3B] hover:bg-[#1E40AF] text-white font-bold uppercase tracking-wider px-6 py-4 rounded-sm" data-testid={`industry-cta-${industry.id}`}>
                          Discuss Your Requirements
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>

                  {/* Products Preview */}
                  <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                    <div className="bg-white border border-slate-200 p-6">
                      <h4 className="font-bold text-[#0B1F3B] uppercase tracking-wider text-sm mb-6">
                        Recommended Products
                      </h4>
                      <div className="space-y-4">
                        {industry.products.map((productSlug) => {
                          const product = getProductBySlug(productSlug);
                          if (!product) return null;
                          return (
                            <Link 
                              key={productSlug}
                              to={`/products/p/${productSlug}`}
                              className="group flex items-center gap-4 p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
                            >
                              <div className="w-20 h-20 flex-shrink-0 overflow-hidden bg-white border border-slate-200">
                                <img 
                                  src={product.image}
                                  alt={product.name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div className="flex-1">
                                <h5 className="font-semibold text-[#0B1F3B] group-hover:text-[#1E40AF] transition-colors">
                                  {product.name}
                                </h5>
                                <p className="text-sm text-slate-500 mt-1 line-clamp-1">
                                  {product.tagline}
                                </p>
                              </div>
                              <ArrowRight className="h-5 w-5 text-slate-400 group-hover:text-[#1E40AF] group-hover:translate-x-1 transition-all" />
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#1E40AF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Manrope'] text-3xl md:text-4xl font-bold text-white uppercase tracking-tight">
              Don't See Your Industry?
            </h2>
            <p className="mt-4 text-white/80 text-lg max-w-2xl mx-auto">
              We provide custom weighing solutions for specialized applications. 
              Contact our engineering team to discuss your unique requirements.
            </p>
            <Link to="/contact">
              <Button className="tech-shimmer mt-8 rounded-sm border border-white/20 bg-white px-8 py-5 font-bold uppercase tracking-wider text-[#0B1F3B] shadow-[0_20px_50px_rgba(2,6,23,0.28)] hover:bg-[#DBEAFE]" data-testid="industries-contact-btn">
                Contact Our Experts
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default IndustriesPage;
